import React from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ChevronLeft } from "lucide-react";
import Seo from "@/components/Seo";

const Section = ({ n, title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 mb-3">{n}. {title}</h2>
    <div className="space-y-3 text-slate-700 leading-relaxed">{children}</div>
  </section>
);

const Sub = ({ n, title, children }) => (
  <div className="mt-5">
    <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2">{n} {title}</h3>
    <div className="space-y-2 text-slate-700 leading-relaxed">{children}</div>
  </div>
);

const Bullet = ({ children }) => <li className="pl-1">{children}</li>;

export default function TermsPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 fade-in">
      <Seo title="İstifadə şərtləri" description="AzeAuksion.az istifadə şərtləri. Platforma elan yerləşdirmə xidməti verir — satış komissiyası tutmur, əmlakı mülkiyyətinə götürmür." path="/terms" />
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600 font-semibold mb-6">
        <ChevronLeft className="h-4 w-4" />Ana səhifə
      </Link>

      <header className="mb-8">
        <div className="text-xs uppercase tracking-widest text-blue-600 font-bold mb-2">Hüquqi sənəd</div>
        <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tighter text-slate-900">AzeAuksion.com — İstifadə Şərtləri</h1>
        <p className="text-slate-500 mt-3 text-sm">Son yenilənmə tarixi: <span className="text-slate-700 font-semibold">01.10.2026</span></p>
      </header>

      <div className="p-5 rounded-2xl bg-amber-50 border border-amber-200 flex gap-3 mb-10">
        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-slate-800">
          Bu sənədi diqqətlə oxuyun. Platformadan istifadə, qeydiyyat, elan yerləşdirmə və ya qiymət təklifi vermək bu şərtlərlə razılaşmaq mənasına gəlir.
        </div>
      </div>

      <article className="prose prose-slate max-w-none">

        <Section n="1" title="Ümumi müddəalar">
          <p>Bu İstifadə Şərtləri AzeAuksion.com internet saytından (bundan sonra "Platforma") istifadə qaydalarını, istifadəçilərin hüquq və vəzifələrini, elan yerləşdirilməsi şərtlərini, auksion mexanizminin iş prinsipini və Platformanın hüquqi statusunu müəyyən edir.</p>
          <p>Platformadan istifadə edən, qeydiyyatdan keçən, elan yerləşdirən və ya auksionda qiymət təklifi verən hər bir şəxs bu şərtlərlə tanış olmalı və onlara əməl etməlidir.</p>
          <p>Platformadan istifadə qanunvericiliyin tələblərinə uyğun həyata keçirilməlidir.</p>
        </Section>

        <Section n="2" title="Platformanın məqsədi və hüquqi statusu">
          <p>AzeAuksion.com istifadəçilərə müxtəlif mallar, nəqliyyat vasitələri, daşınmaz əmlak və digər əmlak barədə elanlar yerləşdirmək, elanları nəzərdən keçirmək və onlayn auksion mexanizmi vasitəsilə qiymət təklifləri vermək imkanı yaradan elektron platformadır.</p>
          <p>Platformanın əsas fəaliyyəti elan yerləşdirmə xidmətinin və qiymət təkliflərinin toplanması üçün texniki imkanların təqdim edilməsindən ibarətdir.</p>
          <p>AzeAuksion.com, ayrıca müqavilə və ya qanun əsasında başqa status yaranmadıqda:</p>
          <ul className="list-disc pl-6 space-y-1">
            <Bullet>Elanlarda göstərilən əmlakın sahibi və ya satıcısı deyil.</Bullet>
            <Bullet>Əmlakın alıcısı deyil.</Bullet>
            <Bullet>Alıcı və ya satıcının nümayəndəsi kimi çıxış etmir.</Bullet>
            <Bullet>Tərəflər arasında alqı-satqı müqaviləsi bağlamır.</Bullet>
            <Bullet>Əmlakın mülkiyyət hüququnun ötürülməsini həyata keçirmir.</Bullet>
            <Bullet>Alıcıdan və ya satıcıdan satış məbləğini qəbul etmir.</Bullet>
            <Bullet>Tərəflər arasında ödəniş və pul köçürmələrini həyata keçirmir.</Bullet>
            <Bullet>Əmlakın çatdırılmasını, saxlanmasını və təhvil verilməsini təşkil etmir.</Bullet>
            <Bullet>Satış məbləğindən komissiya və ya faiz tutmur.</Bullet>
          </ul>
          <p>Platformanın konkret əməliyyat üzrə hüquqi statusu faktiki fəaliyyətinə və tətbiq olunan qanunvericiliyə uyğun müəyyən edilir.</p>
        </Section>

        <Section n="3" title="Elan yerləşdirmə xidməti və ödənişlər">
          <p>AzeAuksion.com elan yerləşdirmə xidmətinə görə istifadəçilərdən əvvəlcədən elan edilmiş tariflər əsasında ödəniş ala bilər.</p>
          <p>Elan yerləşdirmə haqqı Platformanın elan dərc etmək xidmətinə görə alınır və əmlakın satış qiymətindən tutulan komissiya hesab edilmir.</p>
          <p>Platforma elan yerləşdirmə haqqından əlavə, əmlakın satılması nəticəsində əldə olunan məbləğdən faiz və ya komissiya tələb etmir.</p>
          <p>Alıcı ilə satıcı arasında sonradan razılaşma əldə olunması və əmlakın satılması Platformaya əlavə satış komissiyası ödəmək öhdəliyi yaratmır.</p>
          <p>Elan yerləşdirmə xidmətinin qiyməti, elanın dərc müddəti, xidmətin həcmi, uzadılma şərtləri və digər tətbiq olunan ödəniş şərtləri istifadəçiyə ödənişdən əvvəl aydın şəkildə göstərilməlidir.</p>
          <p>Ödənişin qaytarılması, xidmətin dayandırılması və ya elanın silinməsi məsələləri tətbiq olunan qanunvericiliyə və əvvəlcədən açıqlanmış şərtlərə uyğun həll edilir. İstehlakçıların qanunla qorunan hüquqları məhdudlaşdırıla bilməz.</p>
        </Section>

        <Section n="4" title="İstifadəçinin qeydiyyatı və məlumatların düzgünlüyü">
          <p>İstifadəçilər qeydiyyat zamanı və Platformadan istifadə etdikləri müddətdə düzgün, tam və aktual məlumat təqdim etməlidirlər.</p>
          <p>Saxta hesab yaratmaq, başqa şəxsin kimliyindən icazəsiz istifadə etmək, yanlış əlaqə məlumatları təqdim etmək və digər istifadəçiləri aldatmaq qadağandır.</p>
          <p>İstifadəçi hesabının təhlükəsizliyinə diqqət yetirməli və hesabından icazəsiz istifadə şübhəsi yarandıqda Platformaya məlumat verməlidir.</p>
        </Section>

        <Section n="5" title="Elan yerləşdirən şəxsin hüquq və vəzifələri">
          <p>Elan yerləşdirən istifadəçi elanında təqdim etdiyi məlumatların düzgünlüyünə, qanuniliyinə və aktuallığına görə məsuliyyət daşıyır.</p>
          <p>İstifadəçi aşağıdakı tələblərə əməl etməlidir:</p>
          <ul className="list-disc pl-6 space-y-1">
            <Bullet>Elanın məzmununu düzgün və aydın göstərmək.</Bullet>
            <Bullet>Əmlakın vəziyyəti, xüsusiyyətləri, qiyməti və hüquqi statusu barədə yanlış məlumat verməmək.</Bullet>
            <Bullet>Əmlakı satmaq və ya auksiona çıxarmaq üçün müvafiq hüquq və səlahiyyətlərə malik olmaq.</Bullet>
            <Bullet>Əmlakın mövcud qüsurlarını və alıcı üçün əhəmiyyətli olan halları gizlətməmək.</Bullet>
            <Bullet>Başqa şəxslərin mülkiyyət, müəlliflik və digər hüquqlarını pozan şəkil, sənəd və məlumatlardan qanunsuz istifadə etməmək.</Bullet>
            <Bullet>Qanunla qadağan olunmuş əmlak və xidmətlər barədə elan yerləşdirməmək.</Bullet>
            <Bullet>Auksion qaydalarına və qanunvericiliyin tələblərinə əməl etmək.</Bullet>
          </ul>
          <p>Platforma qanunazidd, saxta, aldadıcı və ya digər şəxslərin hüquqlarını pozan elanlara qarşı qaydalarına və qanunvericiliyə uyğun tədbirlər görə bilər.</p>
        </Section>

        <Section n="6" title="Auksionun keçirilməsi və qiymət təklifləri">
          <p>Platformada elan üzrə auksion müəyyən edilmiş başlanğıc və bitmə vaxtı çərçivəsində keçirilə bilər.</p>
          <p>İstifadəçilər auksionda iştirak etməzdən əvvəl elan məlumatlarını, qiymət şərtlərini və auksionun qaydalarını nəzərdən keçirməlidirlər.</p>
          <p>Qiymət təklifləri Platformanın texniki imkanları və elan üzrə müəyyən edilmiş qaydalar əsasında qeydə alınır.</p>
          <p>Auksionda ən yüksək qiymət təklifinin verilməsi həmin istifadəçiyə avtomatik olaraq əmlakı almaq hüququ vermir.</p>
          <p>Auksionun bitməsi də özlüyündə əmlakın satılması və ya alqı-satqı müqaviləsinin bağlanması demək deyil.</p>
          <p>Təkliflərin geri götürülməsi, eyni məbləğli təkliflər, minimum təklif artımı, auksionun uzadılması və texniki nasazlıq halları üzrə konkret qaydalar Platformada ayrıca göstərildiyi halda tətbiq olunur. Saytda olmayan funksiyalar mövcudmuş kimi təqdim edilməməlidir.</p>
        </Section>

        <Section n="7" title={'Auksion bitdikdən sonra satıcının "Bəli" və ya "Xeyr" seçimi'}>
          <p>Auksion müddəti bitdikdən sonra satıcıya yekun qiymət təklifi ilə bağlı "Bəli" və ya "Xeyr" seçimi təqdim edilə bilər.</p>

          <Sub n="7.1." title={'"Bəli" seçiminin mənası'}>
            <p>Satıcının "Bəli" düyməsini seçməsi onun əmlakı satmağa razılıq verdiyi, alıcının təklifini hüquqi baxımdan qəbul etdiyi və ya alıcıya əmlakı almaq hüququ verdiyi mənasına gəlmir.</p>
            <p>"Bəli" seçimi yalnız satıcının yekun qiymət təklifi ilə tanış olduğunu və öz mülahizəsinə əsasən potensial alıcı ilə əlaqə saxlamaq imkanını nəzərdən keçirdiyini bildirir.</p>
            <p>Satıcı "Bəli" seçdikdən sonra potensial alıcı ilə əlaqə saxlamaq və ya saxlamamaq barədə müstəqil qərar verir.</p>
            <p>Satıcı:</p>
            <ul className="list-disc pl-6 space-y-1">
              <Bullet>Potensial alıcı ilə əlaqə saxlamağa məcbur deyil.</Bullet>
              <Bullet>Danışıqları davam etdirməyə məcbur deyil.</Bullet>
              <Bullet>Əmlakı satmağa məcbur deyil.</Bullet>
              <Bullet>Təklif edilən qiyməti qəbul etməyə məcbur deyil.</Bullet>
              <Bullet>Alqı-satqı müqaviləsi bağlamağa yalnız "Bəli" seçiminə görə məcbur edilə bilməz.</Bullet>
            </ul>
            <p>Auksionda ən yüksək qiyməti təklif edən istifadəçi yalnız həmin təklifə görə əmlakın satılmasını tələb etmək və ya satıcını müqavilə bağlamağa məcbur etmək hüququ əldə etmir.</p>
          </Sub>

          <Sub n="7.2." title={'"Xeyr" seçiminin mənası'}>
            <p>Satıcı "Xeyr" seçdikdə bu, onun auksionun yekun qiymət təklifini qəbul etmədiyini və həmin təklif əsasında potensial alıcı ilə əlaqə saxlamaq istəmədiyini bildirir.</p>
            <p>Sistem müvafiq istifadəçiyə təklifinin satıcı tərəfindən qəbul edilmədiyi barədə bildiriş göndərə bilər.</p>
            <p>"Xeyr" seçiminin nəticələri auksionun davam etdirilməsi və ya elanın yenidən yerləşdirilməsi kimi məsələlərdə Platformanın faktiki funksiyalarına uyğun tətbiq edilir.</p>
          </Sub>

          <Sub n="7.3." title="Satıcının cavab verməməsi">
            <p>Satıcının "Bəli" və ya "Xeyr" seçməməsi avtomatik olaraq razılıq və ya imtina hesab edilmir, əgər Platformanın ayrıca dərc edilmiş qaydalarında qanunvericiliyə uyğun başqa prosedur nəzərdə tutulmayıbsa.</p>
          </Sub>

          <Sub n="7.4." title="Tərəflərin sonrakı əlaqəsi">
            <p>Satıcı potensial alıcı ilə əlaqə saxlamaq qərarı verdikdə, tərəflər əlaqə, danışıqlar, qiymət, ödəniş, sənədləşmə və əmlakın təhvil verilməsi məsələlərini özləri müzakirə edirlər.</p>
            <p>Platforma tərəflərin adından danışıqlar aparmır, satış məbləğini qəbul etmir və alqı-satqının baş tutacağına zəmanət vermir.</p>
            <p>Tərəflər arasında hər hansı hüquqi öhdəliyin yaranması tətbiq olunan qanunvericilikdən, tərəflərin iradə ifadəsindən və sonradan əldə etdikləri razılaşmadan asılıdır.</p>
          </Sub>
        </Section>

        <Section n="8" title="Alıcıların hüquq və vəzifələri">
          <p>İstifadəçilər auksionda qiymət təklifi verməzdən əvvəl elan məlumatlarını diqqətlə oxumalıdırlar.</p>
          <p>Alıcılar, mümkün olduğu və tətbiq olunan hüquqi tələblərə uyğun olduğu hallarda, əmlakın vəziyyətini, sənədlərini, satıcının şəxsiyyətini və əmlak üzərində sərəncam vermək səlahiyyətini müstəqil şəkildə yoxlamalıdırlar.</p>
          <p>İstifadəçilərin qanunla qorunan hüquqları bu şərtlərlə məhdudlaşdırılmır.</p>
          <p>Auksionda iştirak etmək əmlakın alınmasına zəmanət vermir və satıcının mütləq əlaqə saxlayacağı anlamına gəlmir.</p>
        </Section>

        <Section n="9" title="Saxta təkliflər və manipulyasiya qadağası">
          <p>Aşağıdakı hərəkətlər qadağandır:</p>
          <ul className="list-disc pl-6 space-y-1">
            <Bullet>Qiyməti süni şəkildə artırmaq məqsədilə saxta hesablarla təkliflər vermək.</Bullet>
            <Bullet>Digər istifadəçilərlə razılaşdırılmış manipulyasiya aparmaq.</Bullet>
            <Bullet>Saxta elanlar yerləşdirmək.</Bullet>
            <Bullet>Başqa şəxsin məlumatlarından icazəsiz istifadə etmək.</Bullet>
            <Bullet>Platformanın texniki işinə müdaxilə etmək.</Bullet>
            <Bullet>Digər istifadəçiləri aldatmaq və ya onların hüquqlarını pozmaq.</Bullet>
            <Bullet>Qanunvericiliyə zidd fəaliyyət göstərmək.</Bullet>
          </ul>
          <p>Platforma belə halları araşdıra və qanunvericiliyə, dərc edilmiş qaydalara uyğun tədbirlər görə bilər.</p>
        </Section>

        <Section n="10" title="Platformanın məsuliyyəti və onun hüdudları">
          <p>Platforma elanların yerləşdirilməsi və qiymət təkliflərinin toplanması üçün texniki imkan təqdim edir.</p>
          <p>Elanlarda göstərilən əmlakın mülkiyyətçisi, faktiki satıcısı və ya alıcısı Platforma deyil. Elan məlumatlarını elan yerləşdirən istifadəçi təqdim edir.</p>
          <p>Qanunla tələb olunan öhdəliklər və Platformanın öz üzərinə götürdüyü öhdəliklər saxlanılmaqla, Platforma aşağıdakı məsələlərə görə məsuliyyət daşımadığını bildirir:</p>
          <ul className="list-disc pl-6 space-y-1">
            <Bullet>Satıcının əmlakı satmaqdan imtina etməsi.</Bullet>
            <Bullet>Satıcının potensial alıcı ilə əlaqə saxlamaması.</Bullet>
            <Bullet>Tərəflərin qiymət və digər şərtlər üzrə razılığa gələ bilməməsi.</Bullet>
            <Bullet>Tərəflərin öz aralarında sonradan əldə etdikləri razılaşmaların icrası.</Bullet>
            <Bullet>Əmlakın keyfiyyəti, vəziyyəti və ya hüquqi statusu barədə istifadəçinin təqdim etdiyi məlumatlar.</Bullet>
            <Bullet>Əmlakın təhvil verilməsi və tərəflər arasında hesablaşmalar.</Bullet>
          </ul>
          <p>Bu müddəalar yalnız qanunvericiliyin icazə verdiyi həddə tətbiq edilir. Platformanın öz qanunazidd hərəkətləri, müqavilə öhdəliklərinin pozulması və ya qanunla istisna edilə bilməyən digər məsuliyyət əsasları aradan qaldırılmır.</p>
        </Section>

        <Section n="11" title="Elanların yoxlanılması və silinməsi">
          <p>Platforma qanunazidd, saxta, aldadıcı və ya digər şəxslərin hüquqlarını pozan elanlar barədə məlumat aldıqda, qanunvericiliyə və öz qaydalarına uyğun olaraq araşdırma apara bilər.</p>
          <p>Zəruri hallarda elan müvəqqəti məhdudlaşdırıla, əlavə sənədlər tələb oluna və ya elan silinə bilər.</p>
          <p>Platforma istifadəçilərin qanuni şikayət və müraciət hüquqlarını məhdudlaşdırmır.</p>
        </Section>

        <Section n="12" title="Fərdi məlumatlar və məxfilik">
          <p>İstifadəçilərin fərdi məlumatlarının toplanması, saxlanılması, işlənilməsi və üçüncü şəxslərə təqdim edilməsi Platformanın Məxfilik Siyasətinə və Azərbaycan Respublikasının tətbiq olunan qanunvericiliyinə uyğun həyata keçirilməlidir.</p>
          <p>İstifadəçilər məlumatların hansı məqsədlərlə işlənildiyi və qanunla nəzərdə tutulmuş hüquqları barədə Məxfilik Siyasətindən məlumat əldə edə bilərlər.</p>
          <p>Platforma faktiki olaraq toplamadığı məlumatları topladığını və ya tətbiq etmədiyi təhlükəsizlik mexanizmlərini tətbiq etdiyini iddia etmir.</p>
        </Section>

        <Section n="13" title="Şikayətlər və mübahisələrin həlli">
          <p>İstifadəçilər saxta elanlar, şübhəli təkliflər, hüquq pozuntuları və Platformanın fəaliyyəti ilə bağlı problemlər barədə Platformanın dərc edilmiş əlaqə vasitələri ilə müraciət edə bilərlər.</p>
          <p>Şikayətlər qanunvericiliyə və tətbiq olunan daxili prosedurlara uyğun araşdırılır.</p>
          <p>İstifadəçilərin səlahiyyətli dövlət orqanlarına və məhkəməyə müraciət etmək hüququ saxlanılır.</p>
        </Section>

        <Section n="14" title="Şərtlərin dəyişdirilməsi">
          <p>Platforma İstifadə Şərtlərini qanunvericiliyə uyğun qaydada yeniləyə bilər.</p>
          <p>Yenilənmiş şərtlər saytda dərc edilməli və müvafiq olduqda qüvvəyə minmə tarixi göstərilməlidir.</p>
          <p>Qanunla tələb olunan hallarda istifadəçilərin yenilənmiş şərtlərlə ayrıca tanış olması və onları qəbul etməsi təmin edilməlidir.</p>
        </Section>

        <Section n="15" title="Tətbiq olunan hüquq">
          <p>Bu İstifadə Şərtləri Azərbaycan Respublikasının qüvvədə olan qanunvericiliyinə uyğun tətbiq edilir.</p>
          <p>Bu şərtlərin hər hansı müddəası etibarsız hesab edilərsə, digər müddəalar qanunvericiliyin yol verdiyi həddə qüvvədə qalır.</p>
          <p>Bu sənəddəki heç bir müddəa istehlakçıların və digər istifadəçilərin qanunla qorunan hüquqlarını ləğv etmək məqsədi daşımır.</p>
        </Section>

        <Section n="16" title="Platformanın hüquqi məlumatları">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-5 text-sm">
            <div><span className="font-bold text-slate-900">VÖEN:</span> <span className="text-slate-700 font-mono">4401898712</span></div>
          </div>
        </Section>

      </article>
    </div>
  );
}
